import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Breadcrumbs } from "@threadws/jute";
import {
  meMeOptions,
  personasCreateMutation,
  personasListQueryKey,
  usersListOptions,
  usersListQueryKey,
  usersSetPersonasMutation,
} from "../../api/generated/@tanstack/react-query.gen";
import { PersonaForm, type PersonaFormValues } from "./PersonaForm";

const empty: PersonaFormValues = { name: "", description: "", rules: "" };

/** Creates a persona and makes it yours, so it shows under "Mine". */
export function CreatePersonaPage() {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const create = useMutation(personasCreateMutation());
  const assign = useMutation(usersSetPersonasMutation());
  const toList = () => navigate("/personas");

  const makeMine = async (personaId: string) => {
    const me = await queryClient.fetchQuery(meMeOptions());
    const users = await queryClient.fetchQuery(usersListOptions());
    const current = users.find((user) => user.id === me.id)?.personaIds ?? [];
    await assign.mutateAsync({ path: { id: me.id }, body: { personaIds: [...current, personaId] } });
  };

  const save = async (body: PersonaFormValues) => {
    const persona = await create.mutateAsync({ body });
    // The persona exists either way; if this fails it just isn't yours yet, and shows under "All".
    const mine = await makeMine(persona.id).then(
      () => true,
      () => false,
    );
    await queryClient.invalidateQueries({ queryKey: personasListQueryKey() });
    await queryClient.invalidateQueries({ queryKey: usersListQueryKey() });
    navigate(mine ? "/personas" : "/personas?show=all");
  };

  return (
    <section className="flex w-full max-w-3xl flex-col gap-6 px-4">
      <Breadcrumbs items={[{ label: "Personas", onClick: toList }, { label: "New persona" }]} />
      <h1 className="text-2xl font-medium">New persona</h1>
      <PersonaForm values={empty} onSubmit={save} onCancel={toList} submitLabel="Create" />
    </section>
  );
}
